import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sidebarConfig, isActivePath } from '../../config/sidebarConfig';
import { hasPermission } from '../../utils/permissionUtils';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const splashData = useSelector((state) => state.splash?.data || {});
  const permissions = splashData.permissions || [];
  const siteTitle = splashData.title || '';
  const sidebarBgColor = splashData.sidebar_bg_color || '';
  const [openSubmenus, setOpenSubmenus] = useState({});

  // Filter sidebar items based on permissions
  const filteredSections = useMemo(() => {
    return sidebarConfig.sections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Check main item permissions
        if (item.permissions && item.permissions.length > 0) {
          if (!hasPermission(item.permissions, permissions)) {
            return false;
          }
        }
        
        // For items with submenu, check if any submenu items are visible
        if (item.submenu) {
          const hasVisibleSubItems = item.submenu.some(subItem => {
            if (!subItem.permissions || subItem.permissions.length === 0) {
              return true;
            }
            return hasPermission(subItem.permissions, permissions);
          });
          return hasVisibleSubItems;
        }
        
        return true;
      })
    })).filter(section => section.items.length > 0); // Remove sections with no visible items
  }, [permissions]);

  // Initialize submenu states based on current path
  useEffect(() => {
    const initialStates = {};
    filteredSections.forEach(section => {
      section.items.forEach(item => {
        if (item.submenu) {
          const isActive = item.submenu.some(subItem => 
            isActivePath(location.pathname, subItem.path, subItem.exact, subItem.otherActivePaths)
          );
          initialStates[item.label] = isActive;
        }
      });
    });
    setOpenSubmenus(initialStates);
  }, [location.pathname, filteredSections]);

  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const renderSubmenu = (submenu, parentPath) => {
    // Filter submenu items based on permissions
    const filteredSubmenu = submenu.filter(item => {
      if (!item.permissions || item.permissions.length === 0) {
        return true; // No permissions required, show item
      }
      return hasPermission(item.permissions, permissions);
    });

    if (filteredSubmenu.length === 0) {
      return null; // Don't render submenu if no items are visible
    }

    return (
      <div className={`nav-submenu ${openSubmenus[parentPath] ? 'show' : 'hide'}`}>
        {filteredSubmenu.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-subitem ${isActivePath(location.pathname, item.path, item.exact, item.otherActivePaths) ? 'active' : ''}`}
          >
            <div className="nav-subitem-content">
              <span>{item.label}</span>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  const renderNavItem = (item) => {
    // Check if item requires permissions
    if (item.permissions && item.permissions.length > 0) {
      if (!hasPermission(item.permissions, permissions)) {
        return null; // Don't render item if user doesn't have required permissions
      }
    }

    if (item.submenu) {
      // Check if submenu has any visible items
      const filteredSubmenu = item.submenu.filter(subItem => {
        if (!subItem.permissions || subItem.permissions.length === 0) {
          return true;
        }
        return hasPermission(subItem.permissions, permissions);
      });

      // Don't render parent item if no submenu items are visible
      if (filteredSubmenu.length === 0) {
        return null;
      }

      return (
        <div className="nav-item-group" key={item.label}>
          <div 
            className={`nav-item ${openSubmenus[item.label] ? 'active' : ''}`}
            onClick={() => toggleSubmenu(item.label)}
          >
            <div className="nav-item-content">
              <i className={item.icon}></i>
              <span>{item.label}</span>
              <i className={`fas fa-chevron-right nav-arrow ${openSubmenus[item.label] ? 'open' : ''}`}></i>
            </div>
          </div>
          {renderSubmenu(item.submenu, item.label)}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-item ${isActivePath(location.pathname, item.path, item.exact) ? 'active' : ''}`}
      >
        <div className="nav-item-content">
          <i className={item.icon}></i>
          <span>{item.label}</span>
        </div>
      </Link>
    );
  };

  return (
    <div
      className={`sidebar ${isOpen ? 'show' : 'hide'}`}
      style={sidebarBgColor ? { backgroundColor: sidebarBgColor } : undefined}
    >
      <div className="sidebar-inner">
        <div className="logo-container">
          <div className="logo-wrapper">
            <Link to="/" className="logo-link">
              <img src="/images/logo-sm.svg" alt="Logo" className="logo" />
              {siteTitle && <span className="site-title">{siteTitle}</span>}
            </Link>
            <button className="menu-toggle" onClick={onClose}>
              <i className="fas fa-bars"></i>
            </button>
          </div>
        </div>

        <div className="nav-sections custom-scrollbar">
          {filteredSections.map((section) => (
            <div className="mb-2" key={section.title}>
              <div className="section-title mb-0">{section.title}</div>
              {section.items.map(renderNavItem)}
            </div>
          ))}
        </div>

        <div className="user-section">
          <div className="user-info">
            <img
              src={user?.avatar || '/images/avatar.jpg'}
              alt="User Avatar"
              className="avatar"
            />
            <div className="user-details">
              <div className="user-name">{user?.name || 'User Name'}</div>
              <div className="user-email">{user?.email || 'user@example.com'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 